<template>
  <v-row>
    <v-col cols="12">
      <v-row v-if="props.patientChart && props.patientChart.length" class="section-container">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Supporting Documents</h3>
        </v-col>

        <v-col v-if="props.patientChart && props.patientChart.length" cols="12">
          <v-table class="">
            <thead>
              <tr>
                <th>Uploaded Documents</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(chart, chartIndex) in props.patientChart" :key="`${chart.label}-${chartIndex}`">
                <td>
                  <div class="">{{ chart.label }}</div>
                </td>
                <td>
                  <div v-if="chart.links && chart.links.length" class="text-right">
                    <v-btn
                      v-for="link in chart.links"
                      :key="link"
                      target="_blank"
                      variant="text"
                      color="outline"
                      prepend-icon="mdi-delete"
                      @click="deleteUploadedFile({ label: chart.label, link: link, storeVar: `patientChart` })">
                      Delete
                    </v-btn>
                    <v-btn
                      v-for="link in chart.links"
                      :key="link"
                      variant="text"
                      color="primary"
                      prepend-icon="mdi-download"
                      @click="openDocument(props.orderKey, link)">
                      Download
                    </v-btn>
                  </div>
                  <div v-else>
                    <div v-if="!props.isEditable" class="">Not Uploaded</div>
                    <div v-else>
                      <UploadFileComponent
                        :download-destination="`patientChart`"
                        :download-label="chart.label"
                        @download-link="addDownloadLink" />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>

        <v-col cols="12" class="d-flex mt-4">
          <v-btn
            :loading="showPreloader"
            color="primary"
            variant="outlined"
            class="mt-n4 mb-4"
            :disabled="!props.orderKey"
            @click="getSupportingDocumentsMergedPdf(props.orderKey)">
            View Supporting Documents
          </v-btn>
        </v-col>
      </v-row>

      <v-alert
        v-if="props.formButtonClicked && !supplementalDocsUploaded() && !props.patientChartNotNeeded"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4 form-validation-alert">
        Please upload supplemental documents before proceeding.
      </v-alert>
    </v-col>

    <v-col cols="12">
      <v-row v-if="props.isEditable" class="section-container">
        <v-col cols="12">
          <SupplementalDocumentsUploadComponent
            :is-editable="props.isEditable"
            :supplemental-docs="supplementalDocs"
            @add-free-form-supplemental-docs="addFreeFormSupplementalDocsDownloadLink" />
        </v-col>
      </v-row>
    </v-col>

    <v-col v-if="props.isEditable || (props.medicalNecessity && props.medicalNecessity.length)" cols="12">
      <v-row class="section-container">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Statement of Medical Necessity</h3>
        </v-col>
        <v-col cols="12">
          <v-table class="">
            <thead>
              <tr>
                <th>Uploaded Document</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Statement of Medical Necessity</td>
                <td>
                  <div v-if="medicalNecessity && medicalNecessity.length" class="text-right">
                    <div v-for="link in medicalNecessity" :key="link">
                      <v-btn
                        variant="text"
                        color="outline"
                        prepend-icon="mdi-delete"
                        @click="deleteUploadedFile({ link: link, storeVar: `medicalNecessity` })">
                        Delete
                      </v-btn>
                      <v-btn
                        variant="text"
                        color="primary"
                        prepend-icon="mdi-download"
                        @click="openDocument(props.orderKey, link)">
                        Download
                      </v-btn>
                    </div>
                  </div>
                  <div v-else>
                    <div v-if="!props.isEditable" class="">Not Uploaded</div>
                    <div v-else>
                      <UploadFileComponent
                        :download-destination="`medicalNecessity`"
                        @download-link="addDownloadLink" />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref } from "vue";
import UploadFileComponent from "@/components/UploadFileComponent";
import SupplementalDocumentsUploadComponent from "@/components/SupplementalDocumentsUploadComponent";
import { orderInfoService } from "@/services/orderInfoService";
import { useDocumentLink } from "@/composables/useDocumentLink";

const props = defineProps({
  patientChart: {
    type: Array,
    default() {
      return [];
    },
  },
  medicalNecessity: {
    type: Array,
    default() {
      return [];
    },
  },
  medicalNecessityTemplate: {
    type: String,
    default() {
      return "";
    },
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  supplementalDocs: {
    type: Array,
    default() {
      return [];
    },
  },
  orderKey: {
    type: String,
    default() {
      return "";
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  patientChartNotNeeded: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const supplementalDocs = ref(props.supplementalDocs);
const { openDocument } = useDocumentLink();

const emit = defineEmits([
  "addUploadedFile",
  "updateGuidelines",
  "add-free-form-supplemental-docs",
  "deleteUploadedFile",
]);

const showPreloader = ref(false);

const addDownloadLink = (downloadLinkInfo) => {
  emit("addUploadedFile", downloadLinkInfo);
};

const deleteUploadedFile = (downloadLinkInfo) => {
  downloadLinkInfo.isDelete = true;
  emit("deleteUploadedFile", downloadLinkInfo);
};

const addFreeFormSupplementalDocsDownloadLink = (downloadLinkInfo) => {
  emit("add-free-form-supplemental-docs", downloadLinkInfo);
};

function supplementalDocsUploaded() {
  return (
    (props.patientChart &&
      props.patientChart.length > 0 &&
      props.patientChart.some((doc) => doc.links && doc.links.length > 0)) ||
    supplementalDocs.value.some((doc) => doc.links && doc.links.length > 0)
  );
}

function getSupportingDocumentsMergedPdf(orderKey) {
  showPreloader.value = true;
  orderInfoService.getSupportingDocumentsPdf(orderKey).then((response) => {
    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
    showPreloader.value = false;
  });
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
</style>
